const API_BASE = {
  rafiq: 'https://app.sanajim.com',
  muwafaq: 'https://muwafaq.sanajim.com',
  sahha: 'https://sahha.sanajim.com',
  labor: 'https://labor.sanajim.com',
  trading: 'http://localhost:3002',
}

const STATS_ENDPOINTS = {
  rafiq: '/api/v1/stats',
  muwafaq: '/api/v1/stats',
  sahha: null,       // Sahha has no /stats — uses /dashboard
  labor: '/api/v1/stats',
  trading: '/api/portfolio',
}

const DASHBOARD_ENDPOINTS = {
  rafiq: null,       // Rafiq has no /dashboard — uses /stats
  muwafaq: '/api/v1/dashboard',
  sahha: '/api/v1/dashboard',
  labor: null,
  trading: null,
}

function getHeaders(token) {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

/**
 * Fetch with AbortController + setTimeout — compatible with older mobile browsers.
 * AbortSignal.timeout() is NOT supported on older Chrome/Safari on Saudi 4G phones.
 */
function fetchWithTimeout(url, options = {}, timeoutMs = 15000) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  return fetch(url, { ...options, signal: controller.signal })
    .then((res) => {
      clearTimeout(timeoutId)
      return res
    })
    .catch((err) => {
      clearTimeout(timeoutId)
      throw err
    })
}

// ─── Health Checks ───

export async function fetchHealth(service, token) {
  try {
    const baseUrl = API_BASE[service] || OPS_API_BASE
    const res = await fetchWithTimeout(`${baseUrl}/health`, {
      headers: getHeaders(token),
      mode: 'cors',
    }, 15000)
    if (!res.ok) return { status: 'degraded', service, statusCode: res.status }
    const data = await res.json()
    return {
      ...data,
      service,
      status: data.status === 'ok' ? 'healthy' : (data.status === 'degraded' ? 'degraded' : 'healthy'),
    }
  } catch (err) {
    if (err.name === 'AbortError') return { status: 'down', service, error: 'timeout' }
    return { status: 'down', service, error: err.message }
  }
}

export async function fetchAllHealth(token) {
  const services = ['rafiq', 'muwafaq', 'sahha', 'labor', 'trading']
  const results = await Promise.all(services.map(s => fetchHealth(s, token)))
  return Object.fromEntries(services.map((s, i) => [s, results[i]]))
}

// ─── Dashboard & Stats Endpoints ───

export async function fetchDashboard(service, token) {
  const endpoint = DASHBOARD_ENDPOINTS[service]
  if (!endpoint) return { status: 'nodata', service }
  try {
    const res = await fetchWithTimeout(`${API_BASE[service]}${endpoint}`, {
      headers: getHeaders(token),
      mode: 'cors',
    }, 15000)
    if (!res.ok) return { status: 'error', service, statusCode: res.status }
    const data = await res.json()
    return { status: 'ok', service, ...data }
  } catch (err) {
    if (err.name === 'AbortError') return { status: 'error', service, error: 'timeout' }
    return { status: 'error', service, error: err.message }
  }
}

export async function fetchStats(service, token) {
  const endpoint = STATS_ENDPOINTS[service]
  if (!endpoint) return { status: 'nodata', service }
  try {
    const res = await fetchWithTimeout(`${API_BASE[service]}${endpoint}`, {
      headers: getHeaders(token),
      mode: 'cors',
    }, 15000)
    if (!res.ok) return { status: 'error', service, statusCode: res.status }
    const data = await res.json()
    return { status: 'ok', service, ...data }
  } catch (err) {
    if (err.name === 'AbortError') return { status: 'error', service, error: 'timeout' }
    return { status: 'error', service, error: err.message }
  }
}

export async function fetchAllDashboards(token) {
  const services = ['rafiq', 'muwafaq', 'sahha', 'labor', 'trading']
  const [dashResults, statsResults] = await Promise.all([
    Promise.all(services.map(s => fetchDashboard(s, token))),
    Promise.all(services.map(s => fetchStats(s, token))),
  ])

  // Merge dashboard + stats for each service
  const merged = {}
  services.forEach((s, i) => {
    const dash = dashResults[i]
    const stats = statsResults[i]
    // Prefer dashboard data, overlay stats on top
    if (dash.status === 'ok' && stats.status === 'ok') {
      merged[s] = { ...dash, ...stats, _sources: ['dashboard', 'stats'] }
    } else if (dash.status === 'ok') {
      merged[s] = { ...dash, _sources: ['dashboard'] }
    } else if (stats.status === 'ok') {
      merged[s] = { ...stats, _sources: ['stats'] }
    } else {
      merged[s] = dash.status !== 'nodata' ? dash : stats
    }
  })
  return merged
}

// ─── Ops API ───

const OPS_API_BASE = 'https://dashboard.sanajim.com/ops-api'

export async function fetchOpsStatus() {
  try {
    const res = await fetchWithTimeout(`${OPS_API_BASE}/api/v1/ops/status`, {
      mode: 'cors',
    }, 10000)
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

export async function fetchOpsLogs(count = 50, filter = 'all') {
  try {
    const params = new URLSearchParams({ count, filter })
    const res = await fetchWithTimeout(`${OPS_API_BASE}/api/v1/ops/logs?${params}`, {
      mode: 'cors',
    }, 10000)
    if (!res.ok) return { entries: [] }
    return await res.json()
  } catch {
    return { entries: [] }
  }
}

export async function fetchRealProjects() {
  try {
    const res = await fetchWithTimeout(`${OPS_API_BASE}/api/v1/ops/projects`, {
      mode: 'cors',
    }, 15000)
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

export { fetchWithTimeout }
